import {useState} from "react";
import {
    Alert,
    MultiSelect,
    Skeleton,
    Stack,
    Text,
    UnstyledButton,
} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useTranslation} from "react-i18next";
import {
    dibAdminApi,
    type PublicNewsArticleV2,
} from "../../dibAdminApi/dibAdminApi";
import {ExperimentalLink} from "../../components/ExperimentalLink";
import {useNewsFilters} from "./useNewsFilters";
import {useNewsGroupings} from "./useNewsGroupings";
import {groupNewsByDate} from "./groupNewsByDate";
import classes from "./NewsPage.module.css";
import {getDateLocale} from "../../utils/dateLocales";

const PAGE_SIZE = 50;
// The news endpoint caps take at 500 server-side; stop offering "show more"
// once the merged list reaches it.
const SERVER_TAKE_CAP = 500;

function NewsItem({item}: {item: PublicNewsArticleV2}) {
    const content = (
        <>
            <span className={classes.itemTitleRow}>
                <Text component="span" className={classes.itemTitle}>
                    {item.title}
                </Text>
                {item.source && (
                    <span className={classes.sourcePill}>{item.source}</span>
                )}
            </span>
            {item.description && (
                <Text className={classes.itemDescription}>
                    {item.description}
                </Text>
            )}
        </>
    );

    return (
        <div className={classes.item}>
            {item.link ? (
                <ExperimentalLink href={item.link} className={classes.itemLink}>
                    {content}
                </ExperimentalLink>
            ) : (
                <div className={classes.itemLink}>{content}</div>
            )}
        </div>
    );
}

function ListSkeleton() {
    return (
        <Stack gap="lg" className={classes.list}>
            {Array.from({length: 6}, (_, i) => (
                <Skeleton key={i} height={48} />
            ))}
        </Stack>
    );
}

type NewsListProps = {
    databaseIds: number[];
    sources: string[];
};

// Holds the paging state; remounted (via key) when filters change so `skip`
// resets in the same render as the new filter args.
function NewsList({databaseIds, sources}: NewsListProps) {
    const {t, i18n} = useTranslation();
    const [skip, setSkip] = useState(0);
    const {data, isLoading, isFetching, isError} =
        dibAdminApi.endpoints.getNews.useQuery({
            databaseIds,
            sources,
            skip,
            take: PAGE_SIZE,
        });

    const locale = getDateLocale(i18n.resolvedLanguage ?? i18n.language ?? "nb");

    if (isLoading) return <ListSkeleton />;

    if (isError) {
        return (
            <Alert color="red" title="Error">
                {t("News.loadError", {defaultValue: "Failed to load news."})}
            </Alert>
        );
    }

    if (!data || data.items.length === 0) {
        return (
            <Text className={classes.empty}>
                {t("News.empty", {defaultValue: "No news found"})}
            </Text>
        );
    }

    const groups = groupNewsByDate(data.items, t, locale);
    const hasMore = data.items.length < Math.min(data.total, SERVER_TAKE_CAP);

    return (
        <div className={classes.list}>
            {groups.map((group) => (
                <div key={group.label}>
                    <Text className={classes.sectionLabel}>{group.label}</Text>
                    {group.items.map((item) => (
                        <NewsItem key={item.topicId} item={item} />
                    ))}
                </div>
            ))}
            {hasMore && (
                <UnstyledButton
                    className={classes.loadMore}
                    disabled={isFetching}
                    onClick={() => setSkip(data.items.length)}
                >
                    {t("Phrase.ShowMore")}
                </UnstyledButton>
            )}
        </div>
    );
}

export function NewsPage() {
    const {t} = useTranslation();
    const isMobile = useMediaQuery("(max-width: 48em)");
    const {sources, setValues} = useNewsFilters();
    const groupings = useNewsGroupings();

    // Desktop filter UI lives in NewsFilterSidebar (DashboardLayout slot);
    // the sources facet query only runs for the mobile select.
    const {data: allSources = []} =
        dibAdminApi.endpoints.getNewsSources.useQuery({}, {skip: !isMobile});

    const filterKey = JSON.stringify({
        databaseIds: groupings.databaseIds,
        sources,
    });

    return (
        <Stack gap="md" className={classes.root}>
            {isMobile && (
                <div className={classes.mobileFilters}>
                    <MultiSelect
                        data={groupings.options.map((o) => ({
                            value: o.slug,
                            label: o.name,
                        }))}
                        value={groupings.selectedSlugs.filter((slug) =>
                            groupings.options.some((o) => o.slug === slug)
                        )}
                        onChange={(values) => setValues("groupings", values)}
                        placeholder={t("News.allAreas", {
                            defaultValue: "All areas",
                        })}
                        clearable
                    />
                    <MultiSelect
                        data={[...new Set([...allSources, ...sources])]}
                        value={sources}
                        onChange={(values) => setValues("sources", values)}
                        placeholder={t("News.allSources", {
                            defaultValue: "All sources",
                        })}
                        clearable
                    />
                </div>
            )}
            {/* On a deep-link with groupings selected, wait for the slug →
                databaseIds mapping before querying, or the first request
                would fetch unfiltered. */}
            {groupings.isLoading && groupings.selectedSlugs.length > 0 ? (
                <ListSkeleton />
            ) : (
                <NewsList
                    key={filterKey}
                    databaseIds={groupings.databaseIds}
                    sources={sources}
                />
            )}
        </Stack>
    );
}
